import {
  ChildEvents,
  MediaMachineContext,
  EventKind,
  SET_COLOR,
  SET_AUTOPLAY,
  SET_GRADIENT,
  FlowObjects,
} from "./types";
import { assign } from "@xstate/immer";
import TextFlowObject from "../../model/TextFlowObject";
import ShapeFlowObject from "../../model/ShapeFlowObject";
import Color from "../../model/Color";
import VideoFlowObject from "../../model/VideoFlowObject";
import typeFaceToStyle from "../../model/FontList";
import ImageFlowObject from "../../model/ImageFlowObject";
import PdfFlowObject from "../../model/PdfFlowObject";

export const setPosition = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    if (event.type === "SET_POSITION") {
      ctx.sendAction = {
        undo: {
          type: "SET_POSITION",
          x: ctx.mmo.frame.x,
          y: ctx.mmo.frame.y,
        },
        redo: { type: "SET_POSITION", x: event.x, y: event.y },
        id: ctx.mmo.id,
        kind: EventKind.Mmo,
      };

      let mmo = ctx.mmo;
      mmo.frame = { ...mmo.frame, x: event.x, y: event.y };

      // Note: COMMIT is handled by commitToParent action in the machine
      // Immer automatically propagates changes since ctx.mmo is a reference
      ctx.mmo = mmo;
    }
  }
);

export const setSize = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction =
      event.type === "SET_SIZE"
        ? {
            undo: {
              type: "SET_SIZE",
              width: ctx.mmo.frame.width,
              height: ctx.mmo.frame.height,
            },
            redo: {
              type: "SET_SIZE",
              width: event.width,
              height: event.height,
            },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_SIZE") {
      mmo.frame = { ...mmo.frame, width: event.width, height: event.height };

      // Note: COMMIT is handled by commitToParent action in the machine
      // Immer automatically propagates changes since ctx.mmo is a reference
    }
    ctx.mmo = mmo;
  }
);

export const setSizeAndPosition = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    if (event.type === "SET_SIZE_AND_POSITION") {
      ctx.sendAction = {
        undo: {
          type: "SET_SIZE_AND_POSITION",
          width: ctx.mmo.frame.width,
          height: ctx.mmo.frame.height,
          x: ctx.mmo.frame.x,
          y: ctx.mmo.frame.y,
        },
        redo: {
          type: "SET_SIZE_AND_POSITION",
          width: event.width,
          height: event.height,
          x: event.x,
          y: event.y,
        },
        id: ctx.mmo.id,
        kind: EventKind.Mmo,
      };

      let mmo = ctx.mmo;
      mmo.frame = {
        ...mmo.frame,
        width: event.width,
        height: event.height,
        x: event.x,
        y: event.y,
      };

      // Note: COMMIT is handled by commitToParent action in the machine
      // Immer automatically propagates changes since ctx.mmo is a reference
      ctx.mmo = mmo;
    }
  }
);

export const setAction = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction =
      event.type === "SET_ACTION"
        ? {
            undo: { type: "SET_ACTION", action: ctx.mmo.action },
            redo: { type: "SET_ACTION", action: event.action },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_ACTION") mmo.action = event.action;
    ctx.mmo = mmo;
  }
);

export const setRotation = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction =
      event.type === "SET_ROTATION"
        ? {
            undo: { type: "SET_ROTATION", rotation: ctx.mmo.rotation },
            redo: { type: "SET_ROTATION", rotation: event.rotation },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_ROTATION") {
      mmo.rotation = event.rotation;
      if (
        mmo.type === "shape" &&
        !(mmo as ShapeFlowObject).shapeType.toLowerCase().includes("rectangle")
      ) {
        (mmo as ShapeFlowObject).rotate(event.rotation);
      }
    }
    ctx.mmo = mmo;
  }
);

export const toggleAutoplay = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction =
      event.type === "SET_AUTOPLAY"
        ? {
            undo: {
              type: SET_AUTOPLAY,
              to: (ctx.mmo as VideoFlowObject).autoPlay,
            },
            redo: { type: SET_AUTOPLAY, to: event.to },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_AUTOPLAY")
      mmo = (mmo as VideoFlowObject).toggleAutoplay();
    ctx.mmo = mmo;
  }
);

export const setAlign = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction =
      event.type === "SET_ALIGN"
        ? {
            undo: {
              type: "SET_ALIGN",
              align: (ctx.mmo as TextFlowObject)
                .attributedString_flbAttributesArray[0].alignment!,
            },
            redo: { type: "SET_ALIGN", align: event.align },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_ALIGN")
      (ctx.mmo as TextFlowObject).changeAlignment(event.align);
    ctx.mmo = mmo;
  }
);

export const setFontSize = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    const textMmo = ctx.mmo as TextFlowObject;
    const prevSize = textMmo.attributedString_flbAttributesArray?.[0]?.size
      ?? textMmo.font?.fontSize ?? 24;
    ctx.sendAction =
      event.type === "SET_FONT_SIZE"
        ? {
            undo: {
              type: "SET_FONT_SIZE",
              size: prevSize,
            },
            redo: { type: "SET_FONT_SIZE", size: event.size },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_FONT_SIZE") {
      (ctx.mmo as TextFlowObject).changeFontSize(event.size);
    }

    ctx.mmo = mmo;
  }
);

export const setFontName = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    const textMmo = ctx.mmo as TextFlowObject;
    const prevName = textMmo.attributedString_flbAttributesArray?.[0]?.name
      ?? textMmo.font?.fontName ?? 'HelveticaNeue';
    ctx.sendAction =
      event.type === "SET_FONT_NAME"
        ? {
            undo: {
              type: "SET_FONT_NAME",
              typeface: prevName,
            },
            redo: { type: "SET_FONT_NAME", typeface: event.typeface },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_FONT_NAME") {
      if (!textMmo.attributedString_flbAttributesArray?.[0] || !textMmo.attributedString_fontArray?.[0]) {
        // Arrays not populated — update font directly
        if (textMmo.font) textMmo.font.fontName = event.typeface;
        ctx.mmo = mmo;
        return;
      }
      (ctx.mmo as TextFlowObject).attributedString_flbAttributesArray[0].setTypeFace(
        event.typeface
      );
      (ctx.mmo as TextFlowObject).attributedString_fontArray[0].setTypeFace(
        event.typeface
      );
      (ctx.mmo as TextFlowObject).attributedString_flbAttributesArray[0].setTypeStyle(
        typeFaceToStyle().get(event.typeface)![0] || ""
      );
      (ctx.mmo as TextFlowObject).attributedString_fontArray[0].setTypeStyle(
        typeFaceToStyle().get(event.typeface)![0] || ""
      );
      if (
        (ctx.mmo as TextFlowObject).attributedString_flbAttributesArray[0].name[
          (ctx.mmo as TextFlowObject).attributedString_flbAttributesArray[0]
            .name.length - 1
        ] === "-"
      ) {
        (ctx.mmo as TextFlowObject).attributedString_flbAttributesArray[0].name = (ctx.mmo as TextFlowObject).attributedString_flbAttributesArray[0].name
          .split("-")
          .join("");
        (ctx.mmo as TextFlowObject).attributedString_fontArray[0].name = (ctx.mmo as TextFlowObject).attributedString_fontArray[0].name
          .split("-")
          .join("");
      }
    }

    ctx.mmo = mmo;
  }
);

export const setFontStyle = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction =
      event.type === "SET_FONT_STYLE"
        ? {
            undo: {
              type: "SET_FONT_STYLE",
              style: (ctx.mmo as TextFlowObject)
                .attributedString_flbAttributesArray[0].__typeStyle!,
            },
            redo: { type: "SET_FONT_STYLE", style: event.style },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_FONT_STYLE") {
      let currFontStyling = (ctx.mmo as TextFlowObject)
        .attributedString_flbAttributesArray[0].name;
      let fname = currFontStyling.split("-")[0];
      const fontName = fname + "-" + event.style;
      (ctx.mmo as TextFlowObject).changeFontName(fontName);
    }

    ctx.mmo = mmo;
  }
);

export const editText = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    let attributes = (ctx.mmo as TextFlowObject)
      .attributedString_flbAttributesArray;
    ctx.sendAction =
      event.type === "EDIT_TEXT"
        ? {
            undo: {
              type: "EDIT_TEXT",
              attributes,
              text: (ctx.mmo as TextFlowObject).text,
              isThemeText: false,
            },
            redo: { ...event },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "EDIT_TEXT") {
      (ctx.mmo as TextFlowObject).text = event.text;
      (ctx.mmo as TextFlowObject).isThemeText = event.isThemeText;
      (ctx.mmo as TextFlowObject).attributedString_flbAttributesArray = event.attributes;

      // Update legacy fields for iOS/Mac app compatibility
      if (event.attributes && event.attributes.length > 0) {
        const firstAttr = event.attributes[0];
        // Update textColor from first attribute's color
        if (firstAttr.color) {
          (ctx.mmo as TextFlowObject).textColor = firstAttr.color.asIOS();
        }
        // Update textAlignment from first attribute's alignment
        if (firstAttr.alignment !== null && firstAttr.alignment !== undefined) {
          (ctx.mmo as TextFlowObject).textAlignment = firstAttr.alignment.toString();
        }
        // Update font from first attribute
        if (firstAttr.name && firstAttr.size) {
          (ctx.mmo as TextFlowObject).font = {
            fontName: firstAttr.name,
            fontSize: firstAttr.size
          };
        }
      }

      if ((ctx.mmo as TextFlowObject).originalImageAsset) {
        (ctx.mmo as TextFlowObject).originalImageAsset = null;
        (ctx.mmo as TextFlowObject).croppedImageAsset = null;
      }
    }

    ctx.mmo = mmo;
  }
);

const colorHelper = (ctx: MediaMachineContext, event: ChildEvents) => {
  if (event.type === SET_COLOR) {
    let sendAction = undefined;
    let mmo = ctx.mmo;
    if (ctx.mmo.type === "text") {
      let color: Color = (ctx.mmo as TextFlowObject)
        .attributedString_flbAttributesArray[0].color!;
      sendAction = {
        redo: { type: SET_COLOR, color: event.color },
        undo: {
          type: SET_COLOR,
          color,
        },
        id: mmo.id,
        kind: EventKind.Mmo,
      };
      (mmo as TextFlowObject).changeColor(event.color);
    } else if (ctx.mmo.type === "shape") {
      sendAction = {
        redo: { type: SET_COLOR, color: event.color },
        undo: {
          type: SET_COLOR,
          color: (ctx.mmo as ShapeFlowObject).color,
        },
        id: mmo.id,
        kind: EventKind.Mmo,
      };
      (ctx.mmo as ShapeFlowObject).color = event.color;
    }

    // Note: COMMIT is handled by commitToParent action in the machine
    // Immer automatically propagates changes since ctx.mmo is a reference

    return {
      ...ctx,
      mmo,
      sendAction,
    };
  } else {
    return ctx;
  }
};

export const setColor = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction = colorHelper(ctx, event).sendAction;
    ctx.mmo = colorHelper(ctx, event).mmo;
  }
);

export const changePreviewImage = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction =
      event.type === "CHANGE_PREVIEW_IMG"
        ? {
            undo: {
              type: "CHANGE_PREVIEW_IMG",
              img: ctx.mmo.croppedImageAsset!,
            },
            redo: { type: "CHANGE_PREVIEW_IMG", img: event.img },
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    console.log("FIRING");
    if (event.type === "CHANGE_PREVIEW_IMG") {
      mmo.croppedImageAsset = event.img;
      mmo.originalImageAsset = event.img;
      mmo.__forceUseImage = true;
    }
    ctx.mmo = mmo;
  }
);

export const changeGalleryAsset = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    event.type === "CHANGE_GALLERY_ASSET"
      ? {
          undo: {
            type: "CHANGE_GALLERY_ASSET",
            gallery: ctx.mmo.galleryAssets!,
          },
          redo: { type: "CHANGE_GALLERY_ASSET", gallery: event.gallery },
          id: ctx.mmo.id,
          kind: EventKind.Mmo,
        }
      : undefined;
    let mmo = ctx.mmo;
    if (event.type === "CHANGE_GALLERY_ASSET") {
      mmo.galleryAssets = event.gallery;
      console.info("dataurl error is after this?");
      mmo.__forceUseImage = true;
    }
    ctx.mmo = mmo;
  }
);


function isAV(mmo: FlowObjects): boolean {
    return mmo.type !== "shape" && mmo.type !== "text" && mmo.type !== "audio"
}

function isAVOrShape(mmo: FlowObjects): boolean {
    return mmo.type !== "text" && mmo.type !== "audio"
}

export const setCornerRadius = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    ctx.sendAction =
      event.type === "SET_CORNER_RADIUS" && isAVOrShape(ctx.mmo)
        ? {
            undo: {
              type: "SET_CORNER_RADIUS",
              radius: (ctx.mmo as (ImageFlowObject | VideoFlowObject | PdfFlowObject | ShapeFlowObject)).cornerRadius,
            },
            redo: { type: "SET_CORNER_RADIUS", radius: event.radius},
            id: ctx.mmo.id,
            kind: EventKind.Mmo,
          }
        : undefined;
    let mmo = ctx.mmo;
    if (event.type === "SET_CORNER_RADIUS" && isAVOrShape(ctx.mmo)) {
        (ctx.mmo as (ImageFlowObject | VideoFlowObject | PdfFlowObject | ShapeFlowObject)).cornerRadius = event.radius;
    }
    ctx.mmo = mmo;
  }
);

export const setBorder = assign<MediaMachineContext, ChildEvents>((ctx: MediaMachineContext, event: ChildEvents) => {
	ctx.sendAction =
		event.type === 'SET_BORDER' && isAVOrShape(ctx.mmo)
			? {
					undo: {
						type: 'SET_BORDER',
						color: (ctx.mmo as ImageFlowObject | VideoFlowObject | PdfFlowObject | ShapeFlowObject).borderColor,
						width: (ctx.mmo as ImageFlowObject | VideoFlowObject | PdfFlowObject | ShapeFlowObject).borderWidth,
					},
					redo: { type: 'SET_BORDER', color: event.color, width: event.width },
					id: ctx.mmo.id,
					kind: EventKind.Mmo,
			  }
			: undefined;
	let mmo = ctx.mmo;
	if (event.type === 'SET_BORDER' && isAVOrShape(ctx.mmo)) {
		(ctx.mmo as ImageFlowObject | VideoFlowObject | PdfFlowObject | ShapeFlowObject).borderColor = event.color;
		(ctx.mmo as ImageFlowObject | VideoFlowObject | PdfFlowObject | ShapeFlowObject).borderWidth = event.width;
	}
	ctx.mmo = mmo;
});

export const setGradient = assign<MediaMachineContext, ChildEvents>(
  (ctx: MediaMachineContext, event: ChildEvents) => {
    if (event.type === SET_GRADIENT && ctx.mmo.type === "shape") {
      const shapeMmo = ctx.mmo as ShapeFlowObject;
      ctx.sendAction = {
        undo: {
          type: SET_GRADIENT,
          gradient: shapeMmo.gradientDef ?? null,
        },
        redo: {
          type: SET_GRADIENT,
          gradient: event.gradient,
        },
        id: ctx.mmo.id,
        kind: EventKind.Mmo,
      };
      shapeMmo.gradientDef = event.gradient ?? undefined;
      ctx.mmo = shapeMmo;
    }
  }
); 

